import { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Typography } from '~/components/common/Typography'
import { NeoSwitch } from '~/components/common/NeoSwitch'
import { AddExamplesModal, type ExampleData } from '../AddExamplesModal'
import { SchemaSection } from '../SchemaSection'
import { FlowsLoadingSkeleton } from '../FlowsLoadingSkeleton'
import { useRepoMetadata, useFlowNames } from '~/hooks/task/useRepoMetadata'
import { CommandType } from '~/protos/agents'
import {
  selectSelectedFlows,
  selectAutoGenerateTestData,
  selectApplicationSchemas,
  selectAdditionalNotes,
  selectFlowRepository,
  updateFlowSelection,
  setAutoGenerateTestData,
  setApplicationSchemas,
  addSchemaToApplication,
  removeSchemaFromApplication,
  clearApplicationSchemas,
  setAdditionalNotes,
  setSuggestedRuntimeSettings,
  type SchemaFile
} from '~/store/integrationTestFlowSlice'
import { parseRepositoryName } from '~/utils/repositoryUtils'

interface Flow {
  id: string
  name: string
  needsTrigger: boolean
  selected: boolean
  hasProvidedData?: boolean
  examples?: ExampleData[]
}

interface ConfigureFlowsSlideProps {
  repository: string
  branch: string
}

export function ConfigureFlowsSlide({ repository, branch }: ConfigureFlowsSlideProps) {
  const dispatch = useDispatch()

  // Get state from Redux
  const selectedFlows = useSelector(selectSelectedFlows)
  const autoGenerateTestData = useSelector(selectAutoGenerateTestData)
  const applicationSchemas = useSelector(selectApplicationSchemas)
  const additionalNotes = useSelector(selectAdditionalNotes)
  const flowRepository = useSelector(selectFlowRepository)

  // Parse repository info for API call
  const [orgName, repoName] = parseRepositoryName(repository)

  // Determine if this is an S3 upload or GitHub repo
  const isS3Upload = flowRepository?.conn_type === 'S3' || Boolean(flowRepository?.s3TarFilePath)
  const s3Path = flowRepository?.s3TarFilePath || ''

  // Extract folder name from S3 path for display
  // Format: "prefix___actual_name.tar.gz" -> show only "actual_name"
  const displayRepoName =
    isS3Upload && s3Path
      ? (() => {
          const filename =
            s3Path
              .split('/')
              .pop()
              ?.replace(/\.tar(\.gz)?$/, '') || 'Uploaded Project'
          const parts = filename.split('___')
          return parts.length > 1 ? parts[1] : filename
        })()
      : repository

  // Fast path: fetch flow names directly from GitHub (bypasses agent service)
  const { flowNames, isFetching: isFlowNamesFetching } = useFlowNames({
    connType: isS3Upload ? 'S3' : flowRepository?.conn_type || 'GITHUB',
    orgName: isS3Upload ? '' : orgName || '',
    repoName: isS3Upload ? '' : repoName || '',
    branchName: isS3Upload ? 'main' : branch || '',
    s3TarFilePath: s3Path,
    currentOption: isS3Upload ? 'upload' : 'codeContext',
    enabled: Boolean(flowRepository && (repository || s3Path))
  })

  // Slow path: fetch supplementary metadata (VM args, runtime versions, applications)
  // This loads in the background and doesn't block the flow list display
  const {
    suggestedVmArgs,
    suggestedJavaVersion,
    suggestedMvnVersion,
    suggestedMinMuleRuntimeVersion,
    applications
  } = useRepoMetadata({
    connType: isS3Upload ? 'S3' : flowRepository?.conn_type || 'GITHUB',
    orgName: isS3Upload ? '' : orgName || '',
    repoName: isS3Upload ? '' : repoName || '',
    branchName: isS3Upload ? 'main' : branch || '',
    commitId: '',
    s3TarFilePath: s3Path,
    currentOption: isS3Upload ? 'upload' : 'codeContext',
    enabled: Boolean(flowRepository && (repository || s3Path)),
    commandType: CommandType.INTEGRATION_TEST
  })

  // Initialize local flows state from Redux
  const [flows, setFlows] = useState<Flow[]>(selectedFlows)

  // Initialize flows from API response
  useEffect(() => {
    if (flowNames && flowNames.length > 0 && flows.length === 0) {
      const newFlows = flowNames.map((flowName, index) => ({
        id: `flow-${index + 1}`,
        name: flowName,
        needsTrigger: /trigger|schedule/i.test(flowName),
        selected: true,
        hasProvidedData: false,
        examples: []
      }))
      setFlows(newFlows)
    }
  }, [flowNames, flows.length])

  // Initialize application schemas from API response
  useEffect(() => {
    if (applications && applications.length > 0) {
      const appSchemas = applications.map((app: any) => ({
        applicationId: app.systemObjectName,
        applicationName: app.systemObjectName,
        schemas: []
      }))
      dispatch(setApplicationSchemas(appSchemas))
    }
  }, [applications, dispatch])

  // Store suggested runtime settings in Redux when they're fetched
  useEffect(() => {
    console.log('[ConfigureFlowsSlide] Suggested runtime settings from API:', {
      suggestedVmArgs,
      suggestedJavaVersion,
      suggestedMvnVersion,
      suggestedMinMuleRuntimeVersion
    })
    if (
      suggestedVmArgs !== undefined ||
      suggestedJavaVersion !== undefined ||
      suggestedMvnVersion !== undefined ||
      suggestedMinMuleRuntimeVersion !== undefined
    ) {
      console.log('[ConfigureFlowsSlide] Dispatching setSuggestedRuntimeSettings to Redux')
      dispatch(
        setSuggestedRuntimeSettings({
          vmArgs: suggestedVmArgs,
          javaVersion: suggestedJavaVersion,
          mvnVersion: suggestedMvnVersion,
          minMuleRuntimeVersion: suggestedMinMuleRuntimeVersion
        })
      )
    }
  }, [
    suggestedVmArgs,
    suggestedJavaVersion,
    suggestedMvnVersion,
    suggestedMinMuleRuntimeVersion,
    dispatch
  ])

  // Loading state: only block on flow names, not supplementary metadata
  const isLoading = isFlowNamesFetching

  // Modal state
  const [modalState, setModalState] = useState<{
    isOpen: boolean
    flowId: string | null
    flowName: string | null
    initialExamples?: ExampleData[]
  }>({
    isOpen: false,
    flowId: null,
    flowName: null
  })

  // Sync local state to Redux when flows change
  useEffect(() => {
    dispatch(updateFlowSelection({ flows }))
  }, [flows, dispatch])

  const handleFlowToggle = (flowId: string) => {
    setFlows(prev =>
      prev.map(flow => (flow.id === flowId ? { ...flow, selected: !flow.selected } : flow))
    )
  }

  const handleAutoGenerateToggle = (checked: boolean) => {
    dispatch(setAutoGenerateTestData(checked))
  }

  const handleAdditionalNotesChange = (value: string) => {
    dispatch(setAdditionalNotes(value))
  }

  const handleSelectAllToggle = () => {
    const allSelected = flows.every(flow => flow.selected)
    setFlows(prev => prev.map(flow => ({ ...flow, selected: !allSelected })))
  }

  const handleProvideData = (flowId: string) => {
    const flow = flows.find(f => f.id === flowId)
    if (flow) {
      setModalState({
        isOpen: true,
        flowId: flow.id,
        flowName: flow.name,
        initialExamples: flow.examples
      })
    }
  }

  const handleSaveExamples = (flowId: string, examples: ExampleData[]) => {
    setFlows(prev =>
      prev.map(flow => (flow.id === flowId ? { ...flow, hasProvidedData: true, examples } : flow))
    )
    setModalState({ isOpen: false, flowId: null, flowName: null })
  }

  const handleCloseModal = () => {
    setModalState({ isOpen: false, flowId: null, flowName: null })
  }

  // Show loading skeleton while flows are being loaded
  if (isLoading) {
    return (
      <FlowsLoadingSkeleton
        repository={repository}
        branch={branch}
        isS3Upload={isS3Upload}
        displayRepoName={displayRepoName}
      />
    )
  }

  const handleUploadSchema = async (applicationId: string) => {
    // Find if this application already has schemas
    const app = applicationSchemas.find(a => a.applicationId === applicationId)

    // If application has existing schemas, clear them first
    if (app && app.schemas.length > 0) {
      dispatch(clearApplicationSchemas({ applicationId }))
    }

    // Create a file input element
    const fileInput = document.createElement('input')
    fileInput.type = 'file'
    fileInput.accept = '.xsd,.json,.yaml,.yml,.xml'
    fileInput.multiple = true // Allow multiple file selection
    fileInput.onchange = async e => {
      const files = (e.target as HTMLInputElement).files
      if (files && files.length > 0) {
        // Read all selected files
        for (let i = 0; i < files.length; i++) {
          const file = files[i]
          try {
            const content = await readFileAsText(file)
            const schema: SchemaFile = {
              id: `schema-${Date.now()}-${i}`,
              name: file.name,
              content
            }
            // Add schema to specific application
            dispatch(addSchemaToApplication({ applicationId, schema }))
          } catch (error) {
            console.error('Error reading file:', file.name, error)
          }
        }
      }
    }
    fileInput.click()
  }

  const readFileAsText = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = e => {
        const content = e.target?.result as string
        resolve(content)
      }
      reader.onerror = e => reject(e)
      reader.readAsText(file)
    })
  }

  return (
    <>
      <div className='flex h-full w-full flex-col items-center overflow-y-auto overflow-x-hidden px-[24px] pb-[436px] pt-[70px]'>
        <div className='flex w-full max-w-[520px] flex-col gap-[48px]'>
          {/* Title Section */}
          <div className='flex w-full flex-col items-center gap-[60px]'>
            <div className='flex w-full flex-col items-center gap-[16px]'>
              <Typography
                fontFamily='geist'
                fontSize='titleLg'
                fontWeight='bold'
                className='text-center text-black'
                style={{ fontSize: '20px', fontWeight: 700, lineHeight: '22px' }}
              >
                Choose & configure flows
              </Typography>
              <Typography
                fontFamily='geistMono'
                fontSize='bodySm'
                fontWeight='semibold'
                className='text-center text-[rgba(0,0,0,0.65)]'
                style={{ fontSize: '12px', fontWeight: 600 }}
              >
                {isS3Upload
                  ? `Uploaded folder: ${displayRepoName}`
                  : `Repo & Branch: ${repository}/${branch}`}
              </Typography>
            </div>
          </div>

          {/* Content Area */}
          <div className='flex w-full flex-col gap-[16px]'>
            <div className='flex w-full flex-col gap-[24px]'>
              {/* Test Data Auto-Generate Card */}
              <div className='flex items-center justify-between gap-[16px] rounded-[16px] border border-[rgba(0,0,0,0.02)] bg-[rgba(189,189,189,0.08)] px-[24px] py-[16px]'>
                <div className='flex flex-1 flex-col gap-[4px]'>
                  <Typography
                    fontFamily='geist'
                    fontSize='bodyMd'
                    fontWeight='semibold'
                    className='text-[rgba(0,0,0,0.88)]'
                    style={{ fontSize: '14px', fontWeight: 600, lineHeight: '18px' }}
                  >
                    Auto-generate test data for the integration tests
                  </Typography>
                  <Typography
                    fontFamily='geist'
                    fontSize='bodySm'
                    fontWeight='medium'
                    className='text-[rgba(0,0,0,0.45)]'
                    style={{ fontSize: '12px', fontWeight: 500, lineHeight: '16px' }}
                  >
                    We create test data for you; turn off to add your own.
                  </Typography>
                </div>
                <div className='shrink-0'>
                  <NeoSwitch
                    checked={autoGenerateTestData}
                    onCheckedChange={handleAutoGenerateToggle}
                  />
                </div>
              </div>

              {/* Additional Notes Section */}
              <div className='flex flex-col gap-[12px] rounded-[24px] bg-[rgba(189,189,189,0.08)] px-[24px] py-[16px]'>
                <Typography
                  fontFamily='geist'
                  fontSize='bodyMd'
                  fontWeight='bold'
                  className='text-[rgba(0,0,0,0.88)]'
                  style={{ fontSize: '14px', fontWeight: 700, lineHeight: '18px' }}
                >
                  Additional Notes
                </Typography>
                <textarea
                  value={additionalNotes || ''}
                  onChange={e => handleAdditionalNotesChange(e.target.value)}
                  placeholder='Add additional notes related to specific test cases to trigger the flow, required client ID/secrets, etc. (optional)...'
                  className='w-full rounded-[12px] border border-[#D6D6D6] bg-white px-[12px] py-[8px] text-[14px] font-medium leading-[22px] text-[rgba(0,0,0,0.88)] placeholder:text-[rgba(0,0,0,0.25)] outline-none focus:border-[#9D7EE6] resize-none'
                  style={{ fontFamily: 'Geist, sans-serif', minHeight: '80px' }}
                />
              </div>

              {/* Add Schema Section */}
              <SchemaSection
                applications={applicationSchemas}
                onUploadSchema={handleUploadSchema}
              />

              {/* Extracted Flows Section */}
              <div className='flex flex-col gap-[16px] rounded-[28px] bg-[rgba(189,189,189,0.08)] px-[24px] py-[16px]'>
                {/* Section Header */}
                <div className='flex items-center justify-between px-[24px] py-[8px]'>
                  <Typography
                    fontFamily='geist'
                    fontSize='bodyMd'
                    fontWeight='bold'
                    className='text-[rgba(0,0,0,0.88)]'
                    style={{ fontSize: '14px', fontWeight: 700, lineHeight: '18px' }}
                  >
                    Extracted flows
                  </Typography>
                  {flows.length > 0 && (
                    <button
                      type='button'
                      onClick={handleSelectAllToggle}
                      className='flex items-center gap-[8px] px-[12px] py-[6px] rounded-[8px] bg-transparent border border-[rgba(0,0,0,0.12)] hover:bg-[rgba(0,0,0,0.04)] cursor-pointer transition-colors'
                    >
                      <Typography
                        fontFamily='geist'
                        fontSize='bodySm'
                        fontWeight='medium'
                        className='text-[rgba(0,0,0,0.65)]'
                        style={{ fontSize: '12px', fontWeight: 500, lineHeight: '16px' }}
                      >
                        {flows.every(flow => flow.selected) ? 'Deselect All' : 'Select All'}
                      </Typography>
                    </button>
                  )}
                </div>

                {/* Flow List */}
                <div className='flex flex-col gap-[8px]'>
                  {flows.length === 0 && (
                    <div className='flex flex-col items-center justify-center gap-[8px] rounded-[16px] bg-[rgba(0,0,0,0.02)] px-[24px] py-[32px]'>
                      <svg
                        width='32'
                        height='32'
                        viewBox='0 0 24 24'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                      >
                        <path
                          d='M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2'
                          stroke='rgba(0,0,0,0.2)'
                          strokeWidth='1.5'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                      <Typography
                        fontFamily='geist'
                        fontSize='bodyMd'
                        fontWeight='semibold'
                        className='text-[rgba(0,0,0,0.45)]'
                        style={{ fontSize: '14px', fontWeight: 600, lineHeight: '22px' }}
                      >
                        No flows found
                      </Typography>
                      <Typography
                        fontFamily='geist'
                        fontSize='bodySm'
                        fontWeight='medium'
                        className='text-center text-[rgba(0,0,0,0.3)]'
                        style={{ fontSize: '12px', fontWeight: 500, lineHeight: '18px' }}
                      >
                        This repository doesn't contain any MuleSoft flows. Try selecting a
                        different repository or branch.
                      </Typography>
                    </div>
                  )}
                  {flows.map(flow => (
                    <div
                      key={flow.id}
                      className='flex h-[48px] flex-col items-start gap-[24px] self-stretch rounded-[16px] bg-[rgba(0,0,0,0.02)] py-[4px]'
                    >
                      <div className='box-border flex items-center justify-between w-full h-full pl-[12px] pr-[12px] overflow-hidden'>
                        {/* Left Section: Checkbox + Name + Badge - Entire area clickable */}
                        <button
                          type='button'
                          onClick={() => handleFlowToggle(flow.id)}
                          className='flex gap-[8px] items-center min-w-0 cursor-pointer bg-transparent border-0 p-0'
                        >
                          {/* Checkbox Icon */}
                          <div className='box-border flex flex-col gap-[4px] items-center justify-center overflow-clip p-0 rounded-[12px] shrink-0 size-[36px]'>
                            <div className='basis-0 box-border flex gap-[8px] grow items-center justify-center min-h-px min-w-px px-[16px] py-[8px] relative shrink-0 w-full'>
                              <div className='relative shrink-0 size-[16px]'>
                                {flow.selected ? (
                                  <svg
                                    viewBox='0 0 14 14'
                                    fill='none'
                                    xmlns='http://www.w3.org/2000/svg'
                                    className='w-full h-full'
                                  >
                                    <path
                                      fillRule='evenodd'
                                      clipRule='evenodd'
                                      d='M0 6.66667C0 2.98477 2.98477 0 6.66667 0C10.3486 0 13.3333 2.98477 13.3333 6.66667C13.3333 10.3486 10.3486 13.3333 6.66667 13.3333C2.98477 13.3333 0 10.3486 0 6.66667ZM10.1381 4.80474L9.19526 3.86193L5.33333 7.72386L3.80474 6.19526L2.86193 7.13807L5.33333 9.60948L10.1381 4.80474Z'
                                      fill='#65558F'
                                    />
                                  </svg>
                                ) : (
                                  <svg
                                    viewBox='0 0 14 14'
                                    fill='none'
                                    xmlns='http://www.w3.org/2000/svg'
                                    className='w-full h-full'
                                  >
                                    <path
                                      fillRule='evenodd'
                                      clipRule='evenodd'
                                      d='M6.66667 1.33333C3.72115 1.33333 1.33333 3.72115 1.33333 6.66667C1.33333 9.61219 3.72115 12 6.66667 12C9.61219 12 12 9.61219 12 6.66667C12 3.72115 9.61219 1.33333 6.66667 1.33333ZM0 6.66667C0 2.98477 2.98477 0 6.66667 0C10.3486 0 13.3333 2.98477 13.3333 6.66667C13.3333 10.3486 10.3486 13.3333 6.66667 13.3333C2.98477 13.3333 0 10.3486 0 6.66667ZM10.1381 4.80474L5.33333 9.60948L2.86193 7.13807L3.80474 6.19526L5.33333 7.72386L9.19526 3.86193L10.1381 4.80474Z'
                                      fill='rgba(38, 38, 38, 0.2)'
                                    />
                                  </svg>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Name + Badge Container */}
                          <div className='flex gap-[8px] items-center min-w-0'>
                            <Typography
                              fontFamily='geist'
                              fontSize='bodyMd'
                              fontWeight='medium'
                              className='text-[rgba(0,0,0,0.88)] truncate min-w-0'
                              style={{ fontSize: '14px', fontWeight: 500, lineHeight: '22px' }}
                            >
                              {flow.name}
                            </Typography>
                            {flow.needsTrigger && (
                              <div className='flex flex-col items-start justify-center shrink-0'>
                                <div className='flex gap-[4px] h-[16px] items-center px-[4px] py-px rounded-[50px] border border-[rgba(212,136,6,0.3)] bg-[#FFF7E6] shrink-0'>
                                  <Typography
                                    fontFamily='geist'
                                    fontSize='bodySm'
                                    fontWeight='bold'
                                    className='whitespace-nowrap text-[#D48806]'
                                    style={{
                                      fontSize: '10px',
                                      fontWeight: 700,
                                      lineHeight: 'normal'
                                    }}
                                  >
                                    NEEDS TRIGGER
                                  </Typography>
                                </div>
                              </div>
                            )}
                          </div>
                        </button>

                        {/* Right Section: Provide data button (only show when auto-generate is disabled) */}
                        {!autoGenerateTestData && (
                          <button
                            type='button'
                            onClick={e => {
                              e.stopPropagation()
                              handleProvideData(flow.id)
                            }}
                            className='box-border flex gap-[8px] h-full items-center justify-center px-[16px] py-[8px] shrink-0 cursor-pointer bg-transparent border-0'
                          >
                            <Typography
                              fontFamily='geist'
                              fontSize='bodyMd'
                              fontWeight='medium'
                              className={
                                flow.hasProvidedData
                                  ? 'text-[rgba(0,0,0,0.88)]'
                                  : 'text-[rgba(0,0,0,0.45)]'
                              }
                              style={{ fontSize: '14px', fontWeight: 500, lineHeight: '22px' }}
                            >
                              {flow.hasProvidedData
                                ? `${flow.examples?.length || 0} set(s) added`
                                : 'Provide data'}
                            </Typography>
                            <div className='shrink-0 size-[16px]'>
                              <svg
                                viewBox='0 0 16 16'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'
                                className='size-full'
                              >
                                <path
                                  d='M6 3.33341L10.6667 8.00008L6 12.6667'
                                  stroke='black'
                                  strokeOpacity={flow.hasProvidedData ? '0.45' : '0.45'}
                                  strokeWidth='1.5'
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                />
                              </svg>
                            </div>
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Examples Modal */}
      <AddExamplesModal
        isOpen={modalState.isOpen}
        onClose={handleCloseModal}
        flowId={modalState.flowId || ''}
        flowName={modalState.flowName || ''}
        initialExamples={modalState.initialExamples}
        onSave={handleSaveExamples}
      />
    </>
  )
}

export default ConfigureFlowsSlide
